import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CONTACT_ROLE_OPTIONS } from '@/types/investor';
import type { InvestorContactCreateData } from '@/types/investor';
import type { PortalRole } from '@/services/portalRoleService';
import { cleanEmail } from '@/utils/validation';
import type { InvestorContactFormErrors } from './investorContactFormValidation';

interface Props {
  formData: InvestorContactCreateData;
  errors: InvestorContactFormErrors;
  onChange: (
    field: keyof InvestorContactCreateData,
    value: string | boolean | null
  ) => void;
  portalRoles: PortalRole[];
}

export function InvestorContactFormFields({
  formData,
  errors,
  onChange,
  portalRoles,
}: Props) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input
                autoFocus
                value={formData.first_name}
                onChange={(e) => onChange('first_name', e.target.value)}
                placeholder="First name"
                className={errors.first_name ? 'border-red-500' : ''}
              />
              {errors.first_name && (
                <p className="text-sm text-red-600">{errors.first_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => onChange('last_name', e.target.value)}
                placeholder="Last name"
                className={errors.last_name ? 'border-red-500' : ''}
              />
              {errors.last_name && (
                <p className="text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => onChange('email', e.target.value)}
                onBlur={(e) => onChange('email', cleanEmail(e.target.value))}
                placeholder="contact@email.com"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => onChange('phone', e.target.value)}
                placeholder="+31 20 123 4567"
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && (
                <p className="text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Mobile</Label>
              <Input
                type="tel"
                value={formData.mobile || ''}
                onChange={(e) => onChange('mobile', e.target.value)}
                placeholder="+31 6 12345678"
                className={errors.mobile ? 'border-red-500' : ''}
              />
              {errors.mobile && (
                <p className="text-sm text-red-600">{errors.mobile}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => onChange('role', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CONTACT_ROLE_OPTIONS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => onChange('title', e.target.value)}
                placeholder="e.g., CFO, Managing Partner"
              />
            </div>

            <div className="space-y-2">
              <Label>Department</Label>
              <Input
                value={formData.department || ''}
                onChange={(e) => onChange('department', e.target.value)}
                placeholder="e.g., Finance, Legal"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is-primary"
                checked={formData.is_primary}
                onChange={(e) => onChange('is_primary', e.target.checked)}
              />
              <Label htmlFor="is-primary">Primary contact</Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is-authorized-signatory"
                checked={formData.is_authorized_signatory}
                onChange={(e) =>
                  onChange('is_authorized_signatory', e.target.checked)
                }
              />
              <Label htmlFor="is-authorized-signatory">
                Authorized signatory
              </Label>
            </div>

            <div className="space-y-2">
              <Label>Portal Access</Label>
              <Select
                value={formData.portal_role_id ?? 'none'}
                onValueChange={(value) =>
                  onChange('portal_role_id', value === 'none' ? null : value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Portal Access</SelectItem>
                  {portalRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t">
            <Label className="text-sm font-medium mb-3 block">
              Notification Preferences
            </Label>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="receives-reports"
                  checked={formData.receives_reports}
                  onChange={(e) =>
                    onChange('receives_reports', e.target.checked)
                  }
                />
                <Label htmlFor="receives-reports">Receive Reports</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="receives-capital-calls"
                  checked={formData.receives_capital_calls}
                  onChange={(e) =>
                    onChange('receives_capital_calls', e.target.checked)
                  }
                />
                <Label htmlFor="receives-capital-calls">
                  Receive Capital Calls
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="receives-distributions"
                  checked={formData.receives_distributions}
                  onChange={(e) =>
                    onChange('receives_distributions', e.target.checked)
                  }
                />
                <Label htmlFor="receives-distributions">
                  Receive Distributions
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Address</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Address Line 1</Label>
              <Input
                value={formData.address_line_1 || ''}
                onChange={(e) => onChange('address_line_1', e.target.value)}
                placeholder="Street address"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Address Line 2</Label>
              <Input
                value={formData.address_line_2 || ''}
                onChange={(e) => onChange('address_line_2', e.target.value)}
                placeholder="Apartment, suite, etc."
              />
            </div>

            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={formData.city || ''}
                onChange={(e) => onChange('city', e.target.value)}
                placeholder="City"
              />
            </div>

            <div className="space-y-2">
              <Label>Postcode</Label>
              <Input
                value={formData.postcode || ''}
                onChange={(e) => onChange('postcode', e.target.value)}
                placeholder="Postcode"
              />
            </div>

            <div className="space-y-2">
              <Label>Country</Label>
              <Input
                value={formData.country || ''}
                onChange={(e) => onChange('country', e.target.value)}
                placeholder="e.g., NLD"
                maxLength={3}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
